import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import Notification from '../components/Notification/Notification';
import { useAuth } from './AuthContext';
import { auth } from '../firebase';
import { createErrorLog } from '../services/errorLogs';

const ErrorReportContext = createContext({
  reportError: () => {},
});

export function useErrorReport() {
  return useContext(ErrorReportContext);
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function resolveErrorParts(errorOrMessage, options = {}) {
  const isError =
    errorOrMessage != null &&
    typeof errorOrMessage === 'object' &&
    (errorOrMessage instanceof Error || typeof errorOrMessage.message === 'string');

  const fromError = isError
    ? String(errorOrMessage.message || '')
    : typeof errorOrMessage === 'string'
      ? errorOrMessage
      : '';

  const overrideDetail =
    options.detail != null && String(options.detail).trim()
      ? String(options.detail).trim()
      : '';
  const detail = overrideDetail || fromError;

  const stack = isError && errorOrMessage.stack ? String(errorOrMessage.stack) : null;

  const userMessage =
    (options.userMessage && String(options.userMessage).trim()) ||
    detail ||
    'Something went wrong.';

  return { userMessage, detail: detail || null, stack };
}

/**
 * Global error popup + Firestore logging.
 * Use reportError(err, { pageLabel, userMessage, source, detail, silent, kind }) from pages.
 */
export function ErrorReportProvider({ children }) {
  const { currentUser } = useAuth();
  const location = useLocation();
  const [notification, setNotification] = useState(null);

  const clearNotification = useCallback(() => setNotification(null), []);

  const reportError = useCallback(
    (errorOrMessage, options = {}) => {
      const { userMessage, detail, stack } = resolveErrorParts(errorOrMessage, options);
      const silent = Boolean(options.silent);
      const kind = options.kind === 'intent' ? 'intent' : 'error';

      if (!silent) {
        setNotification({
          message: escapeHtml(userMessage),
          type: 'error',
        });
      }

      // AuthContext hides anonymous users; vibecheck guests still have auth.currentUser.
      const uid = currentUser?.uid || auth?.currentUser?.uid || null;
      if (!uid) {
        console.error(
          `[errorReport] not logged in; ${silent ? 'log skipped' : 'popup only'}:`,
          userMessage,
          errorOrMessage
        );
        return;
      }

      const payload = {
        message: userMessage,
        detail,
        stack,
        pathname: location.pathname || '/',
        pageTitle: typeof document !== 'undefined' ? document.title || '' : '',
        pageLabel: options.pageLabel || null,
        uid,
        email: currentUser?.email || null,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        source: options.source || 'app',
        kind,
        status: 'new',
      };

      createErrorLog(payload).catch((err) => {
        console.error('[errorReport] failed to persist error log:', err);
      });
    },
    [currentUser?.uid, currentUser?.email, location.pathname]
  );

  const value = useMemo(() => ({ reportError }), [reportError]);

  return (
    <ErrorReportContext.Provider value={value}>
      {children}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type || 'error'}
          onClose={clearNotification}
        />
      )}
    </ErrorReportContext.Provider>
  );
}
